import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { getAllNodes } from "@reducers/nodes/thunk/nodes-thunk";
import { updateFilters, setPageNumber, setPageSize, } from "@reducers/nodes/slice/nodes-slice";
import { useDebounce } from "@ui/utils/useDebounce";
// Map NodesDto to grid-compatible format
const mapNodeToGridData = (node) => {
    var _a;
    return ({
        id: node.id,
        nodeId: node.nodeUid,
        nodeName: node.nodeName || "",
        deviceId: node.accessPointUid || "",
        deviceName: node.accessPointName || "",
        status: node.isActive ? "active" : "inactive",
        isActive: node.isActive,
        nodeTemperature: node.nodeTemprature,
        lowTemperature: node.lowTemperature,
        highTemperature: node.highTemperature,
        nodeType: node.nodeTypeCode,
        nodeBehaviour: node.nodeBehaviour,
        lastUpdate: node.lastUpdated,
        freeRoamingNode: (_a = node.freeRoamingNode) !== null && _a !== void 0 ? _a : false,
    });
};
// Merge temperature updates from SignalR with node data
const mergeTemperatureUpdates = (nodes, temperatureUpdates) => {
    if (Object.keys(temperatureUpdates).length === 0) {
        return nodes;
    }
    return nodes.map((node) => {
        const update = temperatureUpdates[node.nodeId];
        if (update && update.temperature !== undefined) {
            return Object.assign(Object.assign({}, node), { nodeTemperature: update.temperature });
        }
        return node;
    });
};
// Clean filters by removing empty values (empty strings, empty arrays, null, undefined)
const cleanFilters = (filters) => {
    const cleaned = {};
    Object.entries(filters).forEach(([key, value]) => {
        // Skip undefined and null values
        if (value === undefined || value === null) {
            return;
        }
        // Skip empty strings
        if (typeof value === "string" && value === "") {
            return;
        }
        // Skip empty arrays
        if (Array.isArray(value) && value.length === 0) {
            return;
        }
        // Include non-empty values (strings, non-empty arrays, and booleans)
        cleaned[key] = value;
    });
    return cleaned;
};
export const useNodesFilter = () => {
    const dispatch = useAppDispatch();
    // Select state from Redux store
    const { nodes, loading, error, filters, currentPageNumber, currentPageSize, sortColumn, sortDirection, paginationInfo, } = useAppSelector((state) => state.nodes);
    // Get temperature updates from SignalR
    const temperatureUpdates = useAppSelector((state) => state.temperature.updates);
    const [searchTerm, setSearchTerm] = useState(filters.globalSearch || "");
    // Debounce search term to avoid too many API calls
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    // Track last fetched values to prevent duplicate calls
    const lastFetchParams = useRef("");
    // Single useEffect for fetching nodes
    useEffect(() => {
        const filtersToSend = cleanFilters(Object.assign(Object.assign({}, filters), { globalSearch: debouncedSearchTerm || undefined }));
        // Create a key to track if we've already fetched with these exact params
        const fetchKey = JSON.stringify({
            pageNumber: currentPageNumber,
            pageSize: currentPageSize,
            sortColumn,
            sortDirection,
            filters: filtersToSend,
        });
        // Skip if we already fetched with these exact params
        if (lastFetchParams.current === fetchKey) {
            return;
        }
        lastFetchParams.current = fetchKey;
        dispatch(getAllNodes({
            pageNumber: currentPageNumber,
            pageSize: currentPageSize,
            sortColumn: sortColumn || undefined,
            sortDirection: sortDirection || undefined,
            filters: filtersToSend,
        }));
    }, [
        dispatch,
        currentPageNumber,
        currentPageSize,
        sortColumn,
        sortDirection,
        filters,
        debouncedSearchTerm,
    ]);
    // Manual fetch function for external use
    // Accepts optional filters parameter to use instead of Redux state (avoids stale closure)
    const fetchNodes = useCallback((appliedFilters) => {
        const filtersToUse = appliedFilters || filters;
        const filtersToSend = cleanFilters(Object.assign(Object.assign({}, filtersToUse), { globalSearch: debouncedSearchTerm || undefined }));
        dispatch(getAllNodes({
            pageNumber: currentPageNumber,
            pageSize: currentPageSize,
            sortColumn: sortColumn || undefined,
            sortDirection: sortDirection || undefined,
            filters: filtersToSend,
        }));
    }, [
        dispatch,
        currentPageNumber,
        currentPageSize,
        sortColumn,
        sortDirection,
        filters,
        debouncedSearchTerm,
    ]);
    const handleFilterChange = (newFilters) => {
        dispatch(updateFilters(newFilters));
    };
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };
    const handleClearFilters = () => {
        dispatch(updateFilters({
            globalSearch: undefined,
            isActive: undefined,
            nodeName: undefined,
            nodeUid: undefined,
            accessPointUid: undefined,
            accessPointName: undefined,
        }));
        setSearchTerm("");
    };
    const handleToggleActive = (node) => {
        // TODO: Implement update node API call when available
    };
    const handlePageChange = (pageNumber) => {
        dispatch(setPageNumber(pageNumber));
    };
    const handlePageSizeChange = (pageSize) => {
        dispatch(setPageSize(pageSize));
    };
    // Map nodes to grid-compatible format and merge with SignalR temperature updates
    const mappedNodes = useMemo(() => {
        const gridNodes = (nodes || []).map(mapNodeToGridData);
        return mergeTemperatureUpdates(gridNodes, temperatureUpdates);
    }, [nodes, temperatureUpdates]);
    return {
        // Data
        nodes: mappedNodes,
        loading,
        error,
        searchTerm,
        filters,
        paginationInfo,
        currentPageNumber,
        currentPageSize,
        // Handlers
        handleFilterChange,
        handleSearchChange,
        handleClearFilters,
        handleToggleActive,
        handlePageChange,
        handlePageSizeChange,
        // Methods
        fetchNodes,
    };
};
